import { Modal, Form, TreeSelect, InputNumber, Button, message, Tag } from "antd";
import { useForm } from "antd/es/form/Form";
import { usePlanServiceDropdown } from "@/common/components/DynamicSelect";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { currencyAmountValidator } from "@/common/components/MultipleOfValidator";
import { useState } from "react";

interface AddServiceItemModalProps {
    visible: boolean;
    isInNetwork?: boolean;
    medicalProviderId: number | undefined;
    planId?: number | undefined;
    policyHolderId?: number | undefined;
    currencyISOCode?: string | undefined;
    customerId?: number | undefined;
    onClose: () => void;
    onAddService: (service: Service) => void;
    existingServices: Service[];
}

interface Service {
    serviceCategoryItemId: number;
    serviceName: string;
    serviceCategoryId: number;        
    serviceCategoryName: string; 
    amount: number;
    coShare?: number | null;
    coveredAmount?: number | null;
    isAmountCoverage?: boolean | null;
    isServiceCovered: boolean | false;
    selected?: any; // full selected service dropdown item
}

const AddServiceItemModal = ({ visible, medicalProviderId, planId, onClose, onAddService, existingServices, isInNetwork, policyHolderId, currencyISOCode, customerId }: AddServiceItemModalProps) => {
    const [form] = useForm();
    const { t } = useTranslation();
    // const { data: serviceItemsRaw, isLoading: loadingServiceItems } = useServiceItemDropdownDependency(medicalProviderId, planId, isInNetwork, policyHolderId, customerId);

    const { data: serviceItemsRaw, isLoading: loadingServiceItems } = usePlanServiceDropdown(planId, policyHolderId);

    //console.log('serviceItemsRaw', serviceItemsRaw);
    const serviceItems = Array.isArray(serviceItemsRaw) ? serviceItemsRaw : [];

    // build tree data supporting both grouped (categories with services[]) and flat list
    const buildTreeData = () => {
        const existingIds = new Set((existingServices || []).map(s => String(s.serviceCategoryItemId)));
        // If already grouped (each item has `services` array)
        if (serviceItems.length > 0 && Array.isArray((serviceItems as any)[0].services)) {
            return (serviceItems as any[])
                .map((cat) => ({
                    title: cat.serviceCategoryName,
                    key: `cat-${cat.serviceCategoryId}`,
                    value: `cat-${cat.serviceCategoryId}`,
                    selectable: false,
                    children: (cat.services || [])
                        .filter((s: any) => !existingIds.has(String(s.serviceCategoryItemId)))
                        .map((s: any) => ({
                            title: s.serviceCategoryItemName,
                            key: String(s.serviceCategoryItemId),
                            value: String(s.serviceCategoryItemId),
                            isLeaf: true,
                        })),
                }))
                .filter((c: any) => (c.children || []).length > 0);
        }

        // Otherwise group flat list by serviceCategoryId
        const grouped: Record<string, any> = {};
        serviceItems.forEach((item: any) => {
            const catId = item.serviceCategoryId ?? 'uncat';
            if (!grouped[catId]) {
                grouped[catId] = {
                    serviceCategoryId: catId,
                    serviceCategoryName: item.serviceCategoryName || 'Other',
                    services: [],
                };
            }
            grouped[catId].services.push(item);
        });

        return Object.values(grouped)
            .map((cat: any) => ({
                title: cat.serviceCategoryName,
                key: `cat-${cat.serviceCategoryId}`,
                value: `cat-${cat.serviceCategoryId}`,
                selectable: false,
                children: (cat.services || [])
                    .filter((s: any) => !existingIds.has(String(s.serviceCategoryItemId ?? s.value)))
                    .map((s: any) => ({
                        title: s.serviceCategoryItemName || s.label || String(s.value),
                        key: String(s.serviceCategoryItemId ?? s.value),
                        value: String(s.serviceCategoryItemId ?? s.value),
                        isLeaf: true,
                    })),
            }))
            .filter((c: any) => (c.children || []).length > 0);
    };

    const treeData = buildTreeData();

    const [exceptions, setExceptions] = useState<any[]>([]);


    useEffect(() => {
        if (!visible) {
            form.resetFields();
        }
    }, [visible, form]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            const selectedId = String(values.serviceCategoryItemId);
            let selectedService = serviceItems.find(
                (item: any) => String(item.value ?? item.serviceCategoryItemId) === selectedId
            );
            if (!selectedService) {
                // try grouped structure
                for (const cat of (serviceItems as any[])) {
                    if (Array.isArray(cat.services)) {
                        const s = cat.services.find((x: any) => String(x.serviceCategoryItemId) === selectedId);
                        if (s) {
                            selectedService = s;
                            break;
                        }
                    }
                }
            }

            if (!selectedService) {
                message.error("Selected service not found.");
                return;
            }

            // create a cleaned copy to avoid storing ReactNode in `label`
            const cleanedSelected = {
                ...selectedService,
                label: String((selectedService as any).label ?? selectedService.value ?? ""),
            };

            onAddService({
                serviceCategoryItemId: values.serviceCategoryItemId,
                serviceName:
                    (selectedService as any).label ??
                    (selectedService as any).serviceCategoryItemName ??
                    String(values.serviceCategoryItemId),
                serviceCategoryId: (selectedService as any).serviceCategoryId,         
                serviceCategoryName: (selectedService as any).serviceCategoryName,     
                amount: values.amount,
                coShare: (selectedService as any).coShare ?? null,
                coveredAmount: (selectedService as any).coveredAmount ?? null,
                isAmountCoverage: (selectedService as any).isAmountCoverage ?? null,
                isServiceCovered: (selectedService as any).isServiceCovered ?? false,
                selected: cleanedSelected, // cleaned selected object
            });
            form.resetFields();
            onClose();
        });
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    const availableServiceItems = serviceItems?.filter(
        (item: any) => !existingServices.some((s) => s.serviceCategoryItemId === item.value)
    );

    // const coloredOptions = availableServiceItems?.map((item: any) => ({
    //     ...item,
    //     label: (
    //         <span style={{ color: item.optionColor }}>
    //             {item.label}
    //         </span>
    //     )
    // }));

    const amountValidator = (fieldName: string) => ({
        validator: (_: any, value: any) => {
            if (value === undefined || value === null || value === "") return Promise.resolve();
            const num = Number(value);
            if (isNaN(num) || num < 1 || num > 9999999999.99 || !/^\d{1,10}(\.\d{1,2})?$/.test(value.toString())) {
                return Promise.reject(t("invalidField", { field: fieldName }));
            }
            return Promise.resolve();
        },
    });

    return (
        <Modal
            title={t("Add Sub Service")}
            open={visible}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={[
                <Button key="back" onClick={handleCancel}>
                    {t("common.cancel", { defaultValue: t("Cancel") })}
                </Button>,
                <Button key="submit" type="primary" loading={loadingServiceItems} onClick={handleOk}>
                    {t("common.add", { defaultValue: t("Add") })}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="serviceCategoryItemId"
                    label={t("Service")}
                    rules={[{ required: true, message: t("Please select a service") }]}
                >
                    <TreeSelect
                        treeData={treeData}
                        loading={loadingServiceItems}
                        placeholder={t("Select service")}
                        treeDefaultExpandAll
                        allowClear
                        showSearch
                        onChange={(value: any) => {
                            // value will be a string key of the selected node
                            const selectedId = String(value);
                            // Find in flat list first
                            const selectedService = serviceItems.find(
                                (item: any) => String(item.value ?? item.serviceCategoryItemId) === selectedId
                            ) || (() => {
                                // If serviceItems was grouped, try to find by serviceCategoryItemId
                                for (const cat of (serviceItems as any[])) {
                                    if (Array.isArray(cat.services)) {
                                        const s = cat.services.find((x: any) => String(x.serviceCategoryItemId) === selectedId);
                                        if (s) return s;
                                    }
                                }
                                return null;
                            })();

                            //console.log('selectedService', selectedService?.customerExceptions);

                            setExceptions(selectedService?.customerExceptions ?? []);

                            form.setFieldsValue({
                                amount: selectedService?.amount ?? 0,
                                serviceCategoryItemId: Number(selectedId),
                            });
                        }}
                        // only allow selecting leaf nodes (services)
                        treeNodeFilterProp="title"
                        treeNodeLabelProp="title"
                    />
                </Form.Item>
                <Form.Item
                    name="amount"
                    label={t("Amount")}
                    validateFirst={true}   
                    rules={[
                        { required: true, message: t("Please enter an amount") },
                        amountValidator("Amount"),
                        currencyAmountValidator(currencyISOCode, "")
                    ]}
                >
                    <InputNumber style={{ width: "100%" }} placeholder={t("Enter amount")} min={0} />

                </Form.Item>

                {exceptions.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                        {exceptions.map((ex) => (
                            <Tag color="green" key={ex.code}>
                                {ex.code} � {ex.name}
                            </Tag>
                        ))}
                    </div>
                )}

            </Form>
        </Modal>
    );
};

export default AddServiceItemModal;